import { Button, Group, Modal, Stack } from '@mantine/core'
import { useEffect, useRef, useState } from 'react'
import EntitiesForm from './entities-form-logic'
import ContactsForm from './entity-contacts/contacts-form'
import WireInstructionsForm from './entity-wire-instructions/wire-instructions-form'
import { useMultiStepForm } from 'app/hooks/useMultiStepForm'
import { EntityParams } from 'app/models/entity-params'
import IonIcon from '@reacticons/ionicons'
import { ContactParams } from 'app/models/contact-params'
import { WireInstructionParams } from 'app/models/wire-instruction-params'
import { useNavigate } from 'react-router-dom'
import { CustomLoaderPage } from 'app/views/components/loader/loader-page'

type MultiPartFormProps = {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  entityToEdit?: EntityParams
  contactsToEdit?: ContactParams[]
  wireInstructionsToEdit?: WireInstructionParams[]
  withRedirection?: boolean
  /** Show a loader instead of the steps while the lookup data is still being fetched. */
  loading?: boolean
}

// Enum does not have strings assigned to each tab to handle as a step number to make use of the hook useMultiStepForm
enum Tab {
  entity,
  thirdPartyProviders,
  contact,
  wireInstructions,
  signatureBlock,
  deliverySettings,
}

export default function MultiPartForm({ open, setOpen, entityToEdit, contactsToEdit, wireInstructionsToEdit, withRedirection, loading = false }: MultiPartFormProps) {
  const ArrowIcon = () => <IonIcon name="arrow-forward-outline" style={{ color: '#1A56DB' }} />

  const [tab, setTab] = useState<Tab>(Tab.entity)
  const [entity, setEntity] = useState<EntityParams | undefined>(entityToEdit)
  const [submitAction, setSubmitAction] = useState<string | undefined>(undefined);
  const navigate = useNavigate()
  const savedEntityRef = useRef<EntityParams | undefined>(undefined)

  // determine action based on which button was clicked in EntitiesForm ("Save" or "Save and Proceed")
  function handleSubmit(freshEntity?: EntityParams) {
    if (submitAction === 'saveAndSubmit') {
      handleSaveAndSubmit(freshEntity)
    } else {
      handleSaveAndProceed(currentStepIndex, freshEntity)
    }
  }

  // No entity-list reload after a save: saveEntity already puts the saved
  // record in the entity store (the details page's entity and its list item)
  // and the grid's store patches its row. A re-read straight after the save
  // could come from PQS before it projected the edit and put the OLD entity
  // back in the list. An edit is always inactive on the ledger, hence the
  // status in the URL (title case: the header's status pill shows it as is).

  // Save and Proceed throught stepper for adding Contacts and Wire Instructions
  function handleSaveAndProceed(tab: number, freshEntity?: EntityParams) {
    const moveOneStep = tab + 1
    if (!isLastStep) {
      setSubmitAction(undefined)
      next()
      setTab(moveOneStep)
    } else {
      const id = freshEntity?.id ?? entity?.id
      navigate(`/entitymanagement/entity/${id}/Inactive`)
      setOpen(false)
    }
  }

  // Saves and submits the edited Entity Form without entering the stepper for Contacts and Wire Instructions
  function handleSaveAndSubmit(freshEntity?: EntityParams) {
    const id = freshEntity?.id ?? entity?.id
    navigate(`/entitymanagement/entity/${id}/Inactive`)
    setOpen(false)
    setSubmitAction(undefined)
  }

  function handleEntitySubmit(formData: { data: EntityParams }) {
    const freshEntity = formData?.data
    setEntity(freshEntity)
    savedEntityRef.current = freshEntity
    // Pass freshEntity directly to avoid relying on async React state update
    handleSubmit(freshEntity)
  }
  useEffect(() => {
    setEntity(entityToEdit)
  }, [entityToEdit])

  // Hook to handle multiple steps, every step is controlled depending on the Form you are positioned.
  const {
    step,
    isFirstStep,
    isLastStep,
    currentStepIndex,
    goTo,
    /*back,*/ next,
  } = useMultiStepForm([
    <EntitiesForm
      isStepper={true}
      entity={entity ?? entityToEdit}
      onHandleSubmit={handleEntitySubmit}
      closeModal={() => setOpen(!open)}
      creationType="entity"
    />,
    <EntitiesForm
      isStepper={true}
      entity={entity ?? entityToEdit}
      onHandleSubmit={handleEntitySubmit}
      closeModal={() => setOpen(!open)}
      creationType="thirdPartyProviders"
    />,
    <ContactsForm
      entityId={entity?.id ?? entityToEdit?.id ?? ''}
      isStepper={true}
      onHandleSubmit={() => handleSubmit()}
      multipleContacts={contactsToEdit}
    />,
    <WireInstructionsForm
      entityId={entity?.id ?? entityToEdit?.id ?? ''}
      isStepper={true}
      onHandleSubmit={() => handleSubmit()}
      multipleWireInstructions={wireInstructionsToEdit}
      closeModal={() => null}
    />,
    <EntitiesForm
      isStepper={true}
      entity={entity ?? entityToEdit}
      onHandleSubmit={handleEntitySubmit}
      closeModal={() => setOpen(!open)}
      creationType="signatureBlock"
    />,
    <EntitiesForm
      isStepper={true}
      entity={entity ?? entityToEdit}
      onHandleSubmit={handleEntitySubmit}
      closeModal={() => setOpen(!open)}
      creationType="deliverySettings"
    />,
  ])

  // Every time the form opens, reset to the first step and clear saved state
  useEffect(() => {
    if (open) {
      savedEntityRef.current = undefined
    }
    if (!isFirstStep) {
      goTo(Tab.entity)
      setTab(Tab.entity)
    }
  }, [open])

  return (
    <Modal
      className="modal-body create-new-form fit-content-modal"
      opened={open}
      onClose={() => {
        // No reload here either: closing saves nothing.
        if (withRedirection && savedEntityRef.current) {
          navigate(`/entitymanagement/entity/${savedEntityRef.current.id}/Inactive`)
        }
        savedEntityRef.current = undefined
        setOpen(!open)
      }}
      centered={true}
      size="77%"
    >
      <Group
        className="stepper-modal"
        noWrap
        position="apart"
        w="100%"
        h="100%"
        align="flex-start"
      >
        <div className='edit-trades-stepper-wrapper'>

          <Button
            rightIcon={
              currentStepIndex == Tab.entity ? <ArrowIcon /> : null
            }
            classNames={{
              root: currentStepIndex == Tab.entity ? "edit-step-button-selected" : "edit-step-button",
              label: currentStepIndex == Tab.entity ? "edit-step-button-label-selected" : "edit-step-button-label"
            }}
            onClick={() => {
              setTab(Tab.entity)
              goTo(Tab.entity)
            }}
          >
            1. Entity
          </Button>
          <Button
            rightIcon={
              currentStepIndex == Tab.thirdPartyProviders ? <ArrowIcon /> : null
            }
            classNames={{
              root: currentStepIndex == Tab.thirdPartyProviders ? "edit-step-button-selected" : "edit-step-button",
              label: currentStepIndex == Tab.thirdPartyProviders ? "edit-step-button-label-selected" : "edit-step-button-label"
            }}
            onClick={() => {
              setTab(Tab.thirdPartyProviders)
              goTo(Tab.thirdPartyProviders)
            }}
            disabled={!entity}

          >
            <div>2. 3rd Party Providers</div>
          </Button>
          <Button
            rightIcon={
              currentStepIndex == Tab.contact ? <ArrowIcon /> : null
            }
            classNames={{
              root: currentStepIndex == Tab.contact ? "edit-step-button-selected" : "edit-step-button",
              label: currentStepIndex == Tab.contact ? "edit-step-button-label-selected" : "edit-step-button-label"
            }}
            onClick={() => {
              setTab(Tab.contact)
              goTo(Tab.contact)
            }}
            disabled={!entity}
          >
            3. Contacts
          </Button>
          <Button
            rightIcon={
              currentStepIndex == Tab.wireInstructions ? <ArrowIcon /> : null
            }
            classNames={{
              root: currentStepIndex == Tab.wireInstructions ? "edit-step-button-selected" : "edit-step-button",
              label: currentStepIndex == Tab.wireInstructions ? "edit-step-button-label-selected" : "edit-step-button-label"
            }}
            onClick={() => {
              setTab(Tab.wireInstructions)
              goTo(Tab.wireInstructions)
            }}
            disabled={!entity}
          >
            4. Wire Instructions
          </Button>
          <Button
            rightIcon={
              currentStepIndex == Tab.signatureBlock ? <ArrowIcon /> : null
            }
            classNames={{
              root: currentStepIndex == Tab.signatureBlock ? "edit-step-button-selected" : "edit-step-button",
              label: currentStepIndex == Tab.signatureBlock ? "edit-step-button-label-selected" : "edit-step-button-label"
            }}
            onClick={() => {
              setTab(Tab.signatureBlock)
              goTo(Tab.signatureBlock)
            }}
            disabled={!entity}
          >
            5. Signature Block
          </Button>
          <Button
            rightIcon={
              currentStepIndex == Tab.deliverySettings ? <ArrowIcon /> : null
            }
            classNames={{
              root: currentStepIndex == Tab.deliverySettings ? "edit-step-button-selected" : "edit-step-button",
              label: currentStepIndex == Tab.deliverySettings ? "edit-step-button-label-selected" : "edit-step-button-label"
            }}
            onClick={() => {
              setTab(Tab.deliverySettings)
              goTo(Tab.deliverySettings)
            }}
            disabled={!entity}
          >
            6. Delivery Settings
          </Button>
        </div>
        <Stack w="70%" className="form-wrapper">
          {loading ? (
            <CustomLoaderPage />
          ) : (
            <>
              {tab === Tab.entity && <>{step}</>}
              {tab === Tab.thirdPartyProviders && <>{step}</>}
              {tab === Tab.contact && <>{step}</>}
              {tab === Tab.wireInstructions && <>{step}</>}
              {tab === Tab.signatureBlock && <>{step}</>}
              {tab === Tab.deliverySettings && <>{step}</>}
            </>
          )}
        </Stack>
      </Group>
    </Modal>
  )
}